import { LawWorksLogo } from "@/components/ui/LawWorksLogo/LawWorksLogo";
import LogoutButton from "@/components/ui/LogoutButton/LogoutButton";
import {
  Badge,
  Button,
  LoadingSpinner,
  ScrollHint,
  Typography,
} from "@/components/ui";
import { useOnboardingSteps } from "@/components/onboarding/OnboardingStepProvider";
import { cn } from "@/lib/utils/cn";
import {
  createContext,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

type OnboardingStepFrameScrollContextValue = {
  scrollContainerRef: RefObject<HTMLDivElement>;
  scrollToBottom: (behavior?: ScrollBehavior) => void;
};

const OnboardingStepFrameScrollContext =
  createContext<OnboardingStepFrameScrollContextValue | null>(null);

const fallbackScrollRef = { current: null } as RefObject<HTMLDivElement>;

export function useOnboardingStepFrameScroll() {
  const context = useContext(OnboardingStepFrameScrollContext);

  if (!context) {
    return {
      scrollContainerRef: fallbackScrollRef,
      scrollToBottom: () => {},
    };
  }

  return context;
}

type OnboardingStepFrameProps = {
  children: ReactNode;
  nextLabel?: string;
};

export function OnboardingStepFrame({
  children,
  nextLabel = "Continue",
}: OnboardingStepFrameProps) {
  const {
    activeStepIndex,
    activeStep,
    goNext,
    isLast,
    isCompleting,
    setActiveStepId,
    steps,
  } = useOnboardingSteps();

  const heading = activeStep.heading ?? activeStep.label;
  const description = activeStep.description;
  const nextButtonLabel = activeStep.nextLabel ?? nextLabel;
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const scrollElement = scrollContainerRef.current;
    if (!scrollElement) {
      return;
    }
    scrollElement.scrollTo({ top: scrollElement.scrollHeight, behavior });
  }, []);
  const scrollContextValue = useMemo(
    () => ({
      scrollContainerRef,
      scrollToBottom,
    }),
    [scrollContainerRef, scrollToBottom],
  );

  const { id: activeStepId } = activeStep;

  useEffect(() => {
    const scrollElement = scrollContainerRef.current;
    if (!scrollElement) {
      return;
    }
    scrollElement.scrollTo({ top: 0, behavior: "auto" });
  }, [activeStepId]);

  return (
    <div className="bg-muted flex h-screen w-full flex-col overflow-hidden">
      <header className="bg-card border-border w-full border-b">
        <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between px-10 py-2">
          <div className="flex items-center justify-center gap-2">
            <LawWorksLogo className="h-8 w-auto sm:h-9" />
            <Badge variant="secondary">Firm setup</Badge>
          </div>
          <LogoutButton />
        </div>
      </header>
      <div className="flex w-full flex-1 justify-center overflow-hidden px-6 py-8">
        <div className="border-border bg-card flex w-full max-w-[1400px] flex-col overflow-hidden rounded-2xl border shadow-sm md:flex-row">
          <aside className="border-border bg-muted/40 w-full border-b px-6 pt-7 pb-8 md:w-64 md:border-r md:border-b-0">
            <div className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              Steps
            </div>
            <div className="relative mt-4">
              <div className="bg-border absolute top-[6px] bottom-[6px] left-[5px] w-px" />
              <ol className="space-y-7">
                {steps.map((step, index) => {
                  const isActive = index === activeStepIndex;
                  const isCompleted = index < activeStepIndex;

                  return (
                    <li key={step.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveStepId(step.id);
                        }}
                        className={cn(
                          "group flex w-full items-start gap-3 text-left",
                        )}
                        aria-current={isActive ? "step" : undefined}
                      >
                        <div className="relative flex w-3 justify-center">
                          <div
                            className={cn(
                              "mt-1 h-2.5 w-2.5 rounded-full border",
                              isActive && "border-primary bg-primary",
                              !isActive &&
                                isCompleted &&
                                "border-primary/60 bg-primary/60",
                              !isActive &&
                                !isCompleted &&
                                "border-border bg-background",
                            )}
                          />
                        </div>
                        <span
                          className={cn(
                            "-mt-[1px] text-sm leading-5 transition-colors",
                            isActive
                              ? "text-foreground font-semibold"
                              : "text-muted-foreground group-hover:text-foreground",
                          )}
                        >
                          {step.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>
          </aside>
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="px-10 pt-7 pb-6">
              <div className="space-y-3">
                {heading ? (
                  <Typography
                    tag="h2"
                    variant="heading"
                    className="text-primary text-2xl font-semibold tracking-tight sm:text-3xl"
                  >
                    {heading}
                  </Typography>
                ) : null}
                {description ? (
                  <Typography
                    tag="p"
                    className="text-muted-foreground text-sm leading-6 font-medium sm:text-base"
                  >
                    {description}
                  </Typography>
                ) : null}
              </div>
            </div>
            <OnboardingStepFrameScrollContext.Provider
              value={scrollContextValue}
            >
              <ScrollHint
                showOnTop={true}
                className="min-h-0 flex-1"
                contentClassName="h-full min-h-0 overflow-y-auto px-10 pb-8"
                scrollContainerRef={scrollContainerRef}
              >
                <div className="space-y-4 pt-2">{children}</div>
              </ScrollHint>
            </OnboardingStepFrameScrollContext.Provider>
            <footer className="flex w-full flex-col gap-2 px-10 pt-6 pb-6">
              <div className="flex w-full flex-row items-center justify-end gap-3">
                <Button
                  type="button"
                  onClick={goNext}
                  size="sm"
                  disabled={isLast && isCompleting}
                  aria-busy={isLast && isCompleting}
                >
                  {isLast && isCompleting ? (
                    <LoadingSpinner className="text-card" />
                  ) : isLast ? (
                    "Finish"
                  ) : (
                    nextButtonLabel
                  )}
                </Button>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
}
